import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { assert } from 'assert-ts';
import { hostQueryKeys, Hosts, networkQueryKeys, roleQueryKeys, Roles } from '#api';
import { ValidationError } from '#api/errors.ts';
import { makeHostQueryOptions } from '#api/hosts.queries.ts';
import type { AddHostAndEnrollmentCodeResult } from '#api/hosts.ts';
import { enrollmentCodeQueryKeys } from '#api/query-keys.ts';
import { maxRolesQueryOptions } from '#api/roles.queries.ts';
import type { Host, HostStatus } from '#dn-types/host.ts';
import { getIpV6 } from '#util/hostIps.ts';
import { computeHostStatus } from '#util/hostStatus.ts';

type CreateGetStartedHostVariables = { networkID: string; name: string };

async function findRoleIdByName(name: string, queryClient: QueryClient): Promise<string | undefined> {
  // staleTime: 0 forces a fresh read — a role created during a prior failed attempt may not be in
  // any cached list yet (this mutation only invalidates on success), and we must find it to reuse it.
  const { data: roles } = await queryClient.fetchQuery({ ...maxRolesQueryOptions, staleTime: 0 });
  return roles.find((role) => role.name === name)?.id;
}

/**
 * Get the id of the host's ICMP-only `${name}-role` (so the two hosts can ping each other but
 * nothing else is open — matching the flow's "all traffic except ping is blocked" copy), creating
 * it if needed. A prior failed attempt may have already created the role, so a duplicate-name
 * error is treated as "already exists" and the existing role is reused — this keeps the retry path
 * idempotent rather than failing on the second try.
 */
async function resolveOnboardingRoleId(name: string, queryClient: QueryClient): Promise<string> {
  const roleName = `${name}-role`;
  try {
    const role = await Roles.addRole({
      name: roleName,
      description: 'Created with the get-started tutorial',
      firewallRules: [
        {
          protocol: 'ICMP',
          allowedRoleID: null,
          allowedTags: [],
          portRange: null,
          description: 'Allow ping between hosts',
        },
      ],
    });
    // addRole's reword catch always throws, so a resolved value is defined.
    return assert(role, 'expected a role to be created for the onboarding host').id;
  } catch (error) {
    if (
      error instanceof ValidationError &&
      error.errors.some((d) => d.path === 'name' && d.code === 'ERR_DUPLICATE_VALUE')
    ) {
      const existingId = await findRoleIdByName(roleName, queryClient);
      return assert(existingId, 'expected to find the existing onboarding role after a duplicate-name error');
    }
    throw error;
  }
}

/**
 * Create one onboarding host end-to-end: resolve its ICMP-only role, then create the host plus a
 * single-use enrollment code in one atomic call, assigned that role at create time.
 */
async function createGetStartedHost(
  { networkID, name }: CreateGetStartedHostVariables,
  queryClient: QueryClient
): Promise<AddHostAndEnrollmentCodeResult> {
  const roleID = await resolveOnboardingRoleId(name, queryClient);

  const result = await Hosts.addHostAndEnrollmentCode({
    name,
    networkID,
    roleID,
    isLighthouse: false,
    isRelay: false,
    listenPort: 0,
    tags: [],
    configOverrides: [],
  });
  const created = assert(result, 'expected a host and enrollment code to be created');
  // The api allocates a v6 address at create for every host on a v2 network, so a miss is an api
  // bug. Fail hard at this boundary — the throw lands in the step's error + retry UI, and the done
  // step's ping code gets to trust the address exists.
  assert(getIpV6(created.host) !== undefined, 'an onboarding host must have an IPv6 address');
  return created;
}

export type HostCreation = ReturnType<typeof useCreateGetStartedHostMutation>;

export function useCreateGetStartedHostMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: CreateGetStartedHostVariables) => createGetStartedHost(variables, queryClient),
    async onSuccess() {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: hostQueryKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: roleQueryKeys.lists() }),
        // Creating a host bumps the network's hostCount.
        queryClient.invalidateQueries({ queryKey: networkQueryKeys.lists() }),
      ]);
    },
  });
}

/**
 * Poll a freshly-created onboarding host's enrollment code until it's consumed: returns `true` once a
 * dnclient enrolls with it (the code's `exists` flips to `false`). Mirrors the enroll page's poll
 * (usePollForTokenExists in useEnrollmentState), but takes the code we already minted via
 * addHostAndEnrollmentCode rather than minting a second one.
 *
 * Duplicated here (not shared with useEnrollmentState) on purpose: the enroll page mints its own token
 * and redirects on success, while the onboarding flow already holds the code and only needs a connected
 * boolean — sharing would mean bending that hook to both shapes. Revisit if a third consumer appears.
 * The "dwell on success, then advance" beat is a UI concern, owned by the GetStarted component.
 */
export function usePollHostEnrolled(
  hostId: string,
  enrollmentCode: string,
  { enabled = true }: { enabled?: boolean } = {}
): boolean {
  const { data } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- code uniquely identifies the host's token
    queryKey: enrollmentCodeQueryKeys.check(enrollmentCode),
    queryFn: ({ signal }) => Hosts.checkEnrollmentToken(hostId, enrollmentCode, { signal }),
    enabled: enabled && Boolean(hostId) && Boolean(enrollmentCode),
    // Poll while the code is still unused; stop once it's consumed.
    refetchInterval: (query) => (query.state.data?.exists ? 5000 : false),
    // Assume unused until we learn otherwise, so the result starts false.
    initialData: () => ({ exists: true }),
  });
  return data.exists === false;
}

// How often to re-fetch a host's live detail while waiting for it to report online. Enrollment-code
// consumption advances the flow a beat before the host's `lastSeenAt` lands, so the ready screen
// keeps polling each host's detail to flip its status dot from `unenrolled` to `online` for real.
const HOST_STATUS_POLL_MS = 3000;
// Cap the poll so a host that enrolls but never reports in (e.g. dnclient trouble) doesn't re-fetch
// forever. ~2 minutes of attempts, then give up and leave the dot at its last real status. This is a
// point-in-time status screen, not a live monitor — guided recovery for the never-online case is T5's.
const MAX_STATUS_POLLS = 40;

/**
 * Track a created onboarding host's real status. The create mutation only returns the immediate
 * create-time snapshot (`metadata.lastSeenAt: null`), so {@link computeHostStatus} on it can never be
 * `online`. We re-fetch the live host (`GET /v2/hosts/:id`), seeded with the snapshot so the card has
 * a name/IP with no flash, and poll until it actually reports online — deliberately decoupled from
 * enrollment-code consumption: a host can consume its code (enroll) yet not be online (e.g. dnclient
 * trouble), and we'd rather show an honest non-online dot than block the flow on it (that recovery
 * case is T5's). Returns the live host (for the card's name + IP) and its computed status, which is
 * point-in-time: once a host reads online (or the poll budget is spent) we stop re-fetching.
 *
 * GetStarted owns the single instance per host and passes the result down to every consumer (cards,
 * ping section, phone card) — React Query runs `refetchInterval` per observer, so a second observer
 * on the same host would double the request rate and burn the shared poll budget in half the wall
 * time. Accepts null so the flow can call it before the host exists or its status is on screen
 * (polling only runs while enabled); null in, null out.
 */
export function useGetStartedHostStatus(host: Host | null): GetStartedHostStatus | null {
  const { data, dataUpdatedAt } = useQuery({
    ...makeHostQueryOptions(host?.id ?? ''),
    enabled: host !== null,
    initialData: host !== null ? { data: host } : undefined,
    // Poll until the host reports online, then stop — its status is settled for this screen. Bound by
    // a poll count (only real fetches increment it, so it isn't eaten by the wait before the host
    // exists) so a never-online host doesn't re-fetch indefinitely.
    refetchInterval: (query) => {
      const live = query.state.data?.data;
      if (live && computeHostStatus(live, new Date(query.state.dataUpdatedAt)) === 'online') return false;
      if (query.state.dataUpdateCount >= MAX_STATUS_POLLS) return false;
      return HOST_STATUS_POLL_MS;
    },
  });

  if (data === undefined) return null;
  const liveHost = data.data;
  return { host: liveHost, status: computeHostStatus(liveHost, new Date(dataUpdatedAt)) };
}

export type GetStartedHostStatus = { host: Host; status: HostStatus };
